import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Shield, Plus, Trash2, Save, X, Sparkles, CalendarRange, Layers, Loader2, Clock, RefreshCw, BarChart3, Users, LogIn, Globe2 } from "lucide-react";
import { toast } from "sonner";
import {
  adminVerify,
  adminList,
  adminGet,
  adminCreate,
  adminUpdate,
  adminDelete,
  adminBatchGenerate,
  adminSchedulerStatus,
  adminSchedulerRunNow,
  adminAnalytics,
} from "@/lib/api";

const DISCIPLINES_17 = [
  "General Medicine",
  "Cardiovascular",
  "Pulmonary",
  "Gastrointestinal",
  "OBGYN",
  "Orthopedics",
  "Chiropractic",
  "Physical Therapy",
  "Dentistry",
  "Dietetics",
  "Pharmacology",
  "Rheumatology",
  "Neurology",
  "Psychology",
  "Athletic Training",
  "Dermatology",
  "Ophthalmology",
];

const emptyCase = () => ({
  date: new Date().toISOString().slice(0, 10),
  specialty: "Orthopedics",
  difficulty: "medium",
  vignette: "",
  diagnosis: "",
  explanation: "",
  clinical_pearls: [""],
  diagnosis_options: [""],
  questions: [{ text: "", relevant: true }],
});

export default function Admin() {
  const navigate = useNavigate();
  const [token, setToken] = useState(localStorage.getItem("dxdaily:adminToken") || "");
  const [tokenInput, setTokenInput] = useState("");
  const [verified, setVerified] = useState(false);
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // full case object being edited
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [batchLabel, setBatchLabel] = useState(null);
  const [scheduler, setScheduler] = useState(null);
  const [schedulerRunning, setSchedulerRunning] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [analyticsDays, setAnalyticsDays] = useState(14);

  // Verify + load
  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        await adminVerify(token);
        setVerified(true);
        const data = await adminList(token);
        setItems(data.items || []);
      } catch {
        toast.error("Invalid admin token");
        setToken("");
        localStorage.removeItem("dxdaily:adminToken");
      }
    })();
  }, [token]);

  const refresh = async () => {
    const data = await adminList(token);
    setItems(data.items || []);
  };

  const loadScheduler = async () => {
    try {
      const s = await adminSchedulerStatus(token);
      setScheduler(s);
    } catch {
      /* ignore */
    }
  };

  const loadAnalytics = async () => {
    try {
      const a = await adminAnalytics(token, analyticsDays);
      setAnalytics(a);
    } catch {
      /* ignore */
    }
  };

  // Poll scheduler + analytics once verified, then every 30s.
  useEffect(() => {
    if (!verified || !token) return;
    loadScheduler();
    loadAnalytics();
    const id = setInterval(() => {
      loadScheduler();
      loadAnalytics();
    }, 30000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verified, token, analyticsDays]);

  const runSchedulerNow = async () => {
    if (schedulerRunning) return;
    setSchedulerRunning(true);
    try {
      const res = await adminSchedulerRunNow(token);
      const s = res.summary || {};
      toast.success(`Scheduler run: ${s.created ?? 0} created, ${s.failed ?? 0} failed, ${s.skipped ?? 0} skipped`);
      await Promise.all([refresh(), loadScheduler()]);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Scheduler run failed");
    } finally {
      setSchedulerRunning(false);
    }
  };

  const handleSignIn = async () => {
    if (!tokenInput) return;
    try {
      await adminVerify(tokenInput);
      localStorage.setItem("dxdaily:adminToken", tokenInput);
      setToken(tokenInput);
      toast.success("Admin verified");
    } catch {
      toast.error("Invalid admin token");
    }
  };

  const handleEdit = async (caseId) => {
    const doc = await adminGet(token, caseId);
    setEditing(doc);
    setCreating(false);
  };

  const handleNew = () => {
    setEditing(emptyCase());
    setCreating(true);
  };

  const handleDelete = async (caseId) => {
    if (!window.confirm(`Delete ${caseId}?`)) return;
    await adminDelete(token, caseId);
    toast.success("Deleted");
    await refresh();
  };

  const handleSave = async () => {
    if (!editing) return;
    const payload = {
      date: editing.date,
      specialty: editing.specialty,
      difficulty: editing.difficulty,
      vignette: editing.vignette,
      diagnosis: editing.diagnosis,
      explanation: editing.explanation,
      clinical_pearls: editing.clinical_pearls.filter((s) => s.trim()),
      diagnosis_options: editing.diagnosis_options.filter((s) => s.trim()),
      questions: editing.questions
        .filter((q) => q.text.trim())
        .map((q) => ({ text: q.text.trim(), relevant: !!q.relevant })),
    };
    setSaving(true);
    try {
      if (creating) {
        await adminCreate(token, payload);
        toast.success("Case created");
      } else {
        await adminUpdate(token, editing.case_id, payload);
        toast.success("Case updated");
      }
      setEditing(null);
      setCreating(false);
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  // Live-updating Pacific Time clock + countdown so admin knows exactly which
  // PT date each button targets and when the visible case will change.
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 30_000);
    return () => clearInterval(id);
  }, []);

  const PT = "America/Los_Angeles";
  const ymdInZone = (d, tz) => {
    // Reliable YYYY-MM-DD in the given IANA zone via Intl.
    const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone: tz,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).formatToParts(d);
    const get = (t) => parts.find((p) => p.type === t)?.value;
    return `${get("year")}-${get("month")}-${get("day")}`;
  };

  const ptToday = ymdInZone(now, PT);
  const ptTomorrow = ymdInZone(new Date(now.getTime() + 86400000), PT);

  // Next 00:00 PT boundary — compute correctly using Intl parts, DST-safe.
  const nextRolloverPT = (() => {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: PT,
      hour12: false,
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    }).formatToParts(now);
    const get = (t) => Number(parts.find((p) => p.type === t)?.value ?? 0);
    let h = get("hour");
    if (h === 24) h = 0; // some locales report 24 at midnight
    const secondsIntoDayPT = h * 3600 + get("minute") * 60 + get("second");
    const secondsUntilMidnight = 86400 - secondsIntoDayPT;
    return new Date(now.getTime() + secondsUntilMidnight * 1000);
  })();
  const msUntil = Math.max(0, nextRolloverPT.getTime() - now.getTime());
  const hoursUntil = Math.floor(msUntil / 3_600_000);
  const minsUntil = Math.floor((msUntil % 3_600_000) / 60_000);
  const rolloverLocal = nextRolloverPT.toLocaleString(undefined, {
    weekday: "short", month: "short", day: "numeric",
    hour: "numeric", minute: "2-digit",
    timeZoneName: "short",
  });

  const [customDate, setCustomDate] = useState(ptTomorrow);
  useEffect(() => {
    setCustomDate((prev) => (prev < ptToday ? ptTomorrow : prev));
  }, [ptToday, ptTomorrow]);

  const runBatch = async (dateStr, specialties, label) => {
    setBatchLabel(label);
    try {
      const res = await adminBatchGenerate(token, { date: dateStr, specialties });
      const c = res.created?.length ?? 0;
      const f = res.failed?.length ?? 0;
      const s = res.skipped?.length ?? 0;
      toast.success(`${label}: ${c} created${f ? `, ${f} failed` : ""}${s ? `, ${s} skipped` : ""}`);
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Batch generation failed");
    } finally {
      setBatchLabel(null);
    }
  };

  const runGenerateToday = () => runBatch(ptToday, DISCIPLINES_17, `Today · ${ptToday}`);
  const runGenerateTomorrow = () => runBatch(ptTomorrow, DISCIPLINES_17, `Tomorrow · ${ptTomorrow}`);
  const runGenerateCustom = () => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(customDate)) {
      toast.error("Pick a date first");
      return;
    }
    runBatch(customDate, DISCIPLINES_17, `Custom · ${customDate}`);
  };

  if (!token || !verified) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4" style={{ fontFamily: "Manrope, sans-serif" }}>
        <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-8 space-y-5">
          <div className="flex items-center gap-2 text-slate-900">
            <Shield className="h-5 w-5" />
            <span
              className="text-xl font-bold tracking-tight"
              style={{ fontFamily: "Outfit, sans-serif" }}
            >
              Admin
            </span>
          </div>
          <p className="text-sm text-slate-600">
            Enter the admin token to curate cases.
          </p>
          <input
            data-testid="admin-token-input"
            type="password"
            value={tokenInput}
            onChange={(e) => setTokenInput(e.target.value)}
            placeholder="Admin token"
            className="w-full rounded-full border border-slate-300 bg-white px-5 py-3 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-900 focus-visible:ring-offset-2"
          />
          <div className="flex gap-2">
            <button
              data-testid="admin-signin-btn"
              onClick={handleSignIn}
              className="flex-1 rounded-full bg-slate-900 text-white px-5 py-3 text-sm font-medium hover:-translate-y-0.5 active:scale-95 transition-all"
            >
              Continue
            </button>
            <button
              onClick={() => navigate("/")}
              className="rounded-full border border-slate-300 bg-white px-4 py-3 text-sm font-medium text-slate-700 hover:border-slate-500 active:scale-95 transition-all"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-slate-200">
        <div className="max-w-4xl mx-auto flex items-center justify-between px-4 py-3">
          <button
            onClick={() => navigate("/")}
            className="inline-flex items-center gap-2 text-sm font-medium text-slate-700 hover:text-slate-900 active:scale-95 transition-all"
          >
            <ArrowLeft className="h-4 w-4" /> Back
          </button>
          <div className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-slate-900" />
            <span
              className="text-lg font-bold tracking-tight"
              style={{ fontFamily: "Outfit, sans-serif" }}
            >
              Admin
            </span>
          </div>
          <button
            data-testid="admin-new-btn"
            onClick={handleNew}
            className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 text-white px-3 py-1.5 text-xs font-medium hover:-translate-y-0.5 active:scale-95 transition-all"
          >
            <Plus className="h-3.5 w-3.5" /> New case
          </button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6 md:py-10 space-y-6">
        {editing ? (
          <CaseEditor
            data={editing}
            onChange={setEditing}
            onCancel={() => { setEditing(null); setCreating(false); }}
            onSave={handleSave}
            saving={saving}
            isNew={creating}
          />
        ) : (
          <>
            <div>
              <p className="text-[11px] font-mono tracking-[0.2em] uppercase text-slate-500">
                Curated cases
              </p>
              <h1
                className="text-3xl sm:text-4xl font-bold tracking-tighter text-slate-900 mt-1"
                style={{ fontFamily: "Outfit, sans-serif" }}
              >
                {items.length} case{items.length === 1 ? "" : "s"} in rotation.
              </h1>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-4" data-testid="analytics-card">
              <div className="flex items-center gap-2">
                <BarChart3 className="h-4 w-4 text-slate-700" />
                <p className="text-[11px] font-mono tracking-[0.2em] uppercase text-slate-500">
                  Live analytics
                </p>
                <span className="ml-auto inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider text-slate-500">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
                  Live · refreshes every 30s
                </span>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-3">
                  <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-slate-500">
                    <Users className="h-3 w-3" /> Registered users
                  </div>
                  <div className="mt-1 text-2xl font-semibold text-slate-900" data-testid="kpi-total-users">
                    {analytics?.total_registered_users ?? "—"}
                  </div>
                </div>
                <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-3">
                  <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-slate-500">
                    <LogIn className="h-3 w-3" /> Logged-in now
                  </div>
                  <div className="mt-1 text-2xl font-semibold text-slate-900" data-testid="kpi-active-sessions">
                    {analytics?.active_sessions ?? "—"}
                  </div>
                </div>
                <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-3">
                  <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-slate-500">
                    <Globe2 className="h-3 w-3" /> All-time visitors
                  </div>
                  <div className="mt-1 text-2xl font-semibold text-slate-900" data-testid="kpi-visitors">
                    {analytics?.total_unique_visitors ?? "—"}
                  </div>
                </div>
                <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-3">
                  <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-slate-500">
                    <Sparkles className="h-3 w-3" /> Total plays
                  </div>
                  <div className="mt-1 text-2xl font-semibold text-slate-900" data-testid="kpi-plays">
                    {analytics?.total_plays_all_time ?? "—"}
                  </div>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-[11px] font-mono tracking-[0.2em] uppercase text-slate-500">
                    Daily unique players (Pacific date)
                  </p>
                  <select
                    data-testid="analytics-days-select"
                    value={analyticsDays}
                    onChange={(e) => setAnalyticsDays(Number(e.target.value))}
                    className="rounded-md border border-slate-300 bg-white px-2 py-1 text-xs font-mono"
                  >
                    <option value={7}>Last 7 days</option>
                    <option value={14}>Last 14 days</option>
                    <option value={30}>Last 30 days</option>
                  </select>
                </div>
                <div className="overflow-x-auto rounded-xl border border-slate-200">
                  <table className="w-full text-xs" data-testid="analytics-daily-table">
                    <thead className="bg-slate-50 text-slate-500 font-mono uppercase tracking-widest">
                      <tr>
                        <th className="text-left px-3 py-2 font-medium">Date</th>
                        <th className="text-right px-3 py-2 font-medium">Players</th>
                        <th className="text-right px-3 py-2 font-medium">Logged in</th>
                        <th className="text-right px-3 py-2 font-medium">Completed</th>
                        <th className="text-right px-3 py-2 font-medium">Total plays</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {(analytics?.daily || []).slice().reverse().map((d) => {
                        const isToday = d.date === analytics?.today_pt;
                        return (
                          <tr key={d.date} className={isToday ? "bg-emerald-50/40" : ""} data-testid={`analytics-row-${d.date}`}>
                            <td className="px-3 py-2 font-mono text-slate-700">
                              {d.date}
                              {isToday && (
                                <span className="ml-2 rounded-full bg-emerald-100 text-emerald-700 px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-widest">today</span>
                              )}
                            </td>
                            <td className="px-3 py-2 text-right font-semibold text-slate-900 tabular-nums">{d.unique_visitors}</td>
                            <td className="px-3 py-2 text-right text-slate-700 tabular-nums">{d.unique_logged_in}</td>
                            <td className="px-3 py-2 text-right text-slate-700 tabular-nums">{d.completed_plays}</td>
                            <td className="px-3 py-2 text-right text-slate-500 tabular-nums">{d.total_plays}</td>
                          </tr>
                        );
                      })}
                      {(!analytics || analytics.daily.length === 0) && (
                        <tr>
                          <td colSpan={5} className="px-3 py-6 text-center text-slate-400 italic">
                            No plays recorded yet — this table will fill up once players start guessing.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div>
                <p className="text-[11px] font-mono tracking-[0.2em] uppercase text-slate-500 mb-2">
                  Today by discipline · <span className="font-mono">{analytics?.today_pt || "—"}</span>
                </p>
                {analytics?.today_by_discipline?.length ? (
                  <div className="space-y-1.5" data-testid="analytics-today-disciplines">
                    {(() => {
                      const max = Math.max(...analytics.today_by_discipline.map((r) => r.unique_visitors), 1);
                      return analytics.today_by_discipline.map((r) => (
                        <div key={r.specialty} className="flex items-center gap-2">
                          <div className="w-40 shrink-0 text-xs text-slate-700 font-medium">{r.specialty}</div>
                          <div className="flex-1 h-4 rounded-full bg-slate-100 overflow-hidden">
                            <div
                              className="h-full bg-slate-900 rounded-full transition-all"
                              style={{ width: `${(r.unique_visitors / max) * 100}%` }}
                            />
                          </div>
                          <div className="w-14 shrink-0 text-right text-xs font-mono tabular-nums text-slate-700">
                            {r.unique_visitors}
                          </div>
                        </div>
                      ));
                    })()}
                  </div>
                ) : (
                  <p className="text-xs text-slate-400 italic">No plays yet today.</p>
                )}
              </div>

              <p className="text-[10px] text-slate-400">
                Anonymous players are counted via a first-party <code className="font-mono">dxd_vid</code> cookie (no PII). Historic logged-in plays are backfilled from stats sync.
              </p>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-3" data-testid="scheduler-card">
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-slate-700" />
                <p className="text-[11px] font-mono tracking-[0.2em] uppercase text-slate-500">
                  Nightly auto-scheduler
                </p>
                <span className={`ml-auto inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider ${scheduler?.enabled ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-slate-100 text-slate-500 border border-slate-200"}`}>
                  <span className={`h-1.5 w-1.5 rounded-full ${scheduler?.enabled ? "bg-emerald-500" : "bg-slate-400"}`} />
                  {scheduler?.enabled ? "Active" : "Idle"}
                </span>
              </div>
              <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-xs text-slate-700 space-y-1">
                <p>
                  Schedule: <b>{scheduler?.schedule || "Daily at 23:30 America/Los_Angeles"}</b>
                </p>
                <p>
                  Tomorrow (<b className="font-mono">{scheduler?.tomorrow_pt || ptTomorrow}</b>):{" "}
                  <b data-testid="scheduler-ready">
                    {scheduler ? `${scheduler.tomorrow_cases_ready}/${scheduler.specialties_total}` : "—"}
                  </b>{" "}
                  cases ready.
                </p>
                <p className="text-slate-500">
                  Last run:{" "}
                  <span className="font-mono">
                    {scheduler?.last_run_at ? new Date(scheduler.last_run_at).toLocaleString() : "never"}
                  </span>
                  {scheduler?.last_run_summary && (
                    <>
                      {" "}· created {scheduler.last_run_summary.created},{" "}
                      failed {scheduler.last_run_summary.failed},{" "}
                      skipped {scheduler.last_run_summary.skipped}
                    </>
                  )}
                </p>
                <p className="text-slate-500">
                  Next run:{" "}
                  <span className="font-mono">
                    {scheduler?.next_run_at ? new Date(scheduler.next_run_at).toLocaleString() : "—"}
                  </span>
                </p>
              </div>
              <p className="text-sm text-slate-600">
                Runs automatically 30 minutes before midnight PT and pre-generates all 17 disciplines for the next Pacific day. No action needed — but you can trigger it manually below.
              </p>
              <div className="flex flex-wrap gap-2">
                <button
                  data-testid="scheduler-run-now-btn"
                  onClick={runSchedulerNow}
                  disabled={schedulerRunning || scheduler?.running}
                  className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-white text-slate-800 px-4 py-2 text-sm font-medium hover:border-slate-500 hover:-translate-y-0.5 active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                >
                  {(schedulerRunning || scheduler?.running) ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4" />
                  )}
                  Run scheduler now
                </button>
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-3">
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-slate-700" />
                <p className="text-[11px] font-mono tracking-[0.2em] uppercase text-slate-500">
                  AI batch generate
                </p>
              </div>
              <div
                data-testid="utc-info"
                className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-xs text-slate-700 space-y-1"
              >
                <p>
                  Current PT date:{" "}
                  <b data-testid="pt-today" className="font-mono">{ptToday}</b>
                </p>
                <p>
                  Cases roll over in{" "}
                  <b>
                    {hoursUntil}h {minsUntil}m
                  </b>{" "}
                  at 00:00 Pacific ({rolloverLocal} your local time).
                </p>
                <p className="text-slate-500">
                  &quot;Tomorrow&quot; = the next Pacific calendar day —{" "}
                  <b className="font-mono">{ptTomorrow}</b>.
                </p>
              </div>
              <p className="text-sm text-slate-600">
                Each button fires one AI-drafted case per discipline (17 in parallel batches of 4, ~2&nbsp;min total). (date, discipline) pairs that already exist are skipped.
              </p>
              <div className="flex flex-wrap gap-2">
                <button
                  data-testid="batch-today-btn"
                  onClick={runGenerateToday}
                  disabled={!!batchLabel}
                  className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 text-white px-4 py-2 text-sm font-medium hover:-translate-y-0.5 active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                >
                  {batchLabel?.startsWith("Today") ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <CalendarRange className="h-4 w-4" />
                  )}
                  Generate {ptToday} (today PT)
                </button>
                <button
                  data-testid="batch-tomorrow-btn"
                  onClick={runGenerateTomorrow}
                  disabled={!!batchLabel}
                  className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-white text-slate-800 px-4 py-2 text-sm font-medium hover:border-slate-500 hover:-translate-y-0.5 active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                >
                  {batchLabel?.startsWith("Tomorrow") ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Layers className="h-4 w-4" />
                  )}
                  Generate {ptTomorrow} (next 00:00 PT)
                </button>
              </div>
              <div className="border-t border-slate-100 pt-3 space-y-2">
                <p className="text-[11px] font-mono tracking-[0.2em] uppercase text-slate-500">
                  Any specific Pacific date
                </p>
                <div className="flex flex-wrap items-center gap-2">
                  <input
                    data-testid="batch-custom-date"
                    type="date"
                    min={ptToday}
                    value={customDate}
                    onChange={(e) => setCustomDate(e.target.value)}
                    disabled={!!batchLabel}
                    className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-mono disabled:opacity-50"
                  />
                  <button
                    data-testid="batch-custom-btn"
                    onClick={runGenerateCustom}
                    disabled={!!batchLabel || !customDate}
                    className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-white text-slate-800 px-4 py-2 text-sm font-medium hover:border-slate-500 hover:-translate-y-0.5 active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                  >
                    {batchLabel?.startsWith("Custom") ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <CalendarRange className="h-4 w-4" />
                    )}
                    Generate for {customDate || "—"}
                  </button>
                </div>
              </div>
              {batchLabel && (
                <p className="text-xs text-slate-500" data-testid="batch-status">
                  Running: <b>{batchLabel}</b>. Please wait — this can take a couple of minutes.
                </p>
              )}
            </div>

            {items.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-8 text-center text-slate-500">
                No cases yet. Click <b>New case</b> to add one.
              </div>
            ) : (
              <div className="grid gap-3">
                {items.map((it) => (
                  <div
                    key={it.case_id}
                    data-testid={`admin-item-${it.date}`}
                    className="flex items-center justify-between rounded-2xl border border-slate-200 bg-white p-4"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-[11px] font-mono tracking-[0.2em] uppercase text-slate-500">
                          {it.date}
                        </span>
                        <span className="text-[10px] font-mono tracking-widest uppercase text-slate-500 border border-slate-200 rounded-full px-2 py-0.5">
                          {it.specialty}
                        </span>
                        <span className="text-[10px] font-mono tracking-widest uppercase text-slate-500 border border-slate-200 rounded-full px-2 py-0.5">
                          {it.difficulty}
                        </span>
                      </div>
                      <p className="text-sm font-semibold text-slate-900 mt-1 truncate" style={{ fontFamily: "Outfit, sans-serif" }}>
                        {it.diagnosis}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0 ml-3">
                      <button
                        data-testid={`admin-edit-${it.date}`}
                        onClick={() => handleEdit(it.case_id)}
                        className="rounded-full border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:border-slate-500 active:scale-95 transition-all"
                      >
                        Edit
                      </button>
                      <button
                        data-testid={`admin-delete-${it.date}`}
                        onClick={() => handleDelete(it.case_id)}
                        className="p-2 rounded-full text-red-600 hover:bg-red-50 active:scale-95 transition-all"
                        aria-label="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}

function CaseEditor({ data, onChange, onCancel, onSave, saving, isNew }) {
  const set = (k, v) => onChange({ ...data, [k]: v });
  const setArr = (k, i, v) => {
    const next = [...data[k]];
    next[i] = v;
    onChange({ ...data, [k]: next });
  };
  const addArr = (k, empty) => onChange({ ...data, [k]: [...data[k], empty] });
  const rmArr = (k, i) => onChange({ ...data, [k]: data[k].filter((_, j) => j !== i) });

  const relevantCount = data.questions.filter((q) => q.relevant).length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1
          className="text-2xl font-bold tracking-tight"
          style={{ fontFamily: "Outfit, sans-serif" }}
        >
          {isNew ? "New case" : `Edit ${data.date}`}
        </h1>
        <div className="flex gap-2">
          <button
            onClick={onCancel}
            className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:border-slate-500 active:scale-95 transition-all"
          >
            <X className="h-4 w-4" /> Cancel
          </button>
          <button
            data-testid="admin-save-btn"
            onClick={onSave}
            disabled={saving}
            className="inline-flex items-center gap-1.5 rounded-full bg-emerald-600 text-white px-5 py-2 text-sm font-medium hover:bg-emerald-700 hover:-translate-y-0.5 active:scale-95 transition-all disabled:opacity-50"
          >
            <Save className="h-4 w-4" /> {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>

      <Section title="Meta">
        <div className="grid sm:grid-cols-3 gap-3">
          <Field label="Date">
            <input
              type="date"
              value={data.date}
              onChange={(e) => set("date", e.target.value)}
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
              disabled={!isNew}
            />
          </Field>
          <Field label="Specialty">
            <input
              value={data.specialty}
              onChange={(e) => set("specialty", e.target.value)}
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
            />
          </Field>
          <Field label="Difficulty">
            <select
              value={data.difficulty}
              onChange={(e) => set("difficulty", e.target.value)}
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
            >
              <option value="easy">Easy</option>
              <option value="medium">Medium</option>
              <option value="hard">Hard</option>
            </select>
          </Field>
        </div>
      </Section>

      <Section title="Clinical condition (shown to players)">
        <textarea
          value={data.vignette}
          onChange={(e) => set("vignette", e.target.value)}
          rows={4}
          className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm leading-relaxed"
        />
      </Section>

      <Section title="Correct diagnosis + explanation">
        <div className="space-y-3">
          <Field label="Correct diagnosis (must also appear in options)">
            <input
              value={data.diagnosis}
              onChange={(e) => set("diagnosis", e.target.value)}
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
            />
          </Field>
          <Field label="Explanation">
            <textarea
              value={data.explanation}
              onChange={(e) => set("explanation", e.target.value)}
              rows={3}
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
            />
          </Field>
        </div>
      </Section>

      <Section title="Clinical pearls" onAdd={() => addArr("clinical_pearls", "")}>
        <div className="space-y-2">
          {data.clinical_pearls.map((p, i) => (
            <div key={i} className="flex gap-2">
              <input
                value={p}
                onChange={(e) => setArr("clinical_pearls", i, e.target.value)}
                className="flex-1 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
              />
              <button
                onClick={() => rmArr("clinical_pearls", i)}
                className="p-2 text-red-600 rounded-lg hover:bg-red-50"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      </Section>

      <Section title={`Diagnosis options (${data.diagnosis_options.length})`} onAdd={() => addArr("diagnosis_options", "")}>
        <div className="grid sm:grid-cols-2 gap-2">
          {data.diagnosis_options.map((o, i) => (
            <div key={i} className="flex gap-2">
              <input
                value={o}
                onChange={(e) => setArr("diagnosis_options", i, e.target.value)}
                className="flex-1 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
              />
              <button
                onClick={() => rmArr("diagnosis_options", i)}
                className="p-2 text-red-600 rounded-lg hover:bg-red-50"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      </Section>

      <Section
        title={`Questions (${data.questions.length} total, ${relevantCount} relevant)`}
        onAdd={() => addArr("questions", { text: "", relevant: false })}
      >
        <div className="space-y-2">
          {data.questions.map((q, i) => (
            <div key={i} className="flex items-center gap-2">
              <button
                onClick={() => {
                  const next = [...data.questions];
                  next[i] = { ...q, relevant: !q.relevant };
                  onChange({ ...data, questions: next });
                }}
                className={`shrink-0 w-16 rounded-full px-2 py-1.5 text-[10px] font-mono tracking-widest uppercase ${
                  q.relevant ? "bg-emerald-500 text-white" : "bg-red-500 text-white"
                }`}
                aria-label="Toggle relevance"
              >
                {q.relevant ? "Yes" : "No"}
              </button>
              <input
                value={q.text}
                onChange={(e) => {
                  const next = [...data.questions];
                  next[i] = { ...q, text: e.target.value };
                  onChange({ ...data, questions: next });
                }}
                className="flex-1 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
              />
              <button
                onClick={() => rmArr("questions", i)}
                className="p-2 text-red-600 rounded-lg hover:bg-red-50"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      </Section>

      <p className="text-xs text-slate-500">
        Recommended: 10 relevant + 20 irrelevant questions (min 20 total, min 3 relevant).
      </p>
    </div>
  );
}

function Section({ title, children, onAdd }) {
  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-[11px] font-mono tracking-[0.2em] uppercase text-slate-500">
          {title}
        </h3>
        {onAdd && (
          <button
            onClick={onAdd}
            className="inline-flex items-center gap-1 text-xs font-medium text-slate-700 hover:text-slate-900"
          >
            <Plus className="h-3.5 w-3.5" /> Add
          </button>
        )}
      </div>
      {children}
    </section>
  );
}

function Field({ label, children }) {
  return (
    <label className="block space-y-1">
      <span className="text-[11px] font-mono tracking-[0.2em] uppercase text-slate-500">
        {label}
      </span>
      {children}
    </label>
  );
}
